import { TrashIcon } from '@heroicons/react/24/outline';
import type { EdgeProps } from '@xyflow/react';
import { EdgeLabelRenderer, getBezierPath } from '@xyflow/react';
import { MouseEvent, useMemo, useState } from 'react';

import { useQueryDataOptional } from '@/sections/insights/queryBuilder/state/useQueryData';
import type { FlatGraphEdge } from '@/types/FlatGraph';

import { EdgeDetailsModal } from './EdgeDetailsModal';

/**
 * Custom data type for FlowEdge
 * Provided by useGraphTransformation hook
 */
interface FlowEdgeData {
  isExpanded?: boolean;
  onToggleModal?: () => void;
  editable?: boolean;
  edgeData?: FlatGraphEdge['data'];
}

/**
 * FlowEdge - Bezier edge with relationship label
 *
 * Displays relationship type on edge path with multi-label indicator
 * Renders inline modal when expanded
 *
 * Modal expansion state received via data props from useGraphTransformation
 */

export function FlowEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: EdgeProps) {
  const context = useQueryDataOptional();

  // Extract props from edge data (provided by useGraphTransformation)
  const edgeData = (data || {}) as FlowEdgeData;
  const isExpanded = edgeData?.isExpanded ?? false;
  const onToggleModal = edgeData?.onToggleModal;
  const editable = edgeData?.editable ?? true;
  const flatEdgeData = edgeData?.edgeData;
  const direction = (edgeData?.edgeData as FlatGraphEdge | undefined)
    ?.direction;

  // Hover state for visual feedback
  const [isHovered, setIsHovered] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    onToggleModal?.();
  };

  const handleDeleteClick = (e: MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (context) {
      context.dispatch({ type: 'REMOVE_EDGE', edgeId: id });
    }
    setShowDeleteConfirm(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  // Compute label text
  const labelText = useMemo(() => {
    if (!data?.label) return null;

    if (typeof data.label === 'string') {
      return data.label;
    }

    if (Array.isArray(data.label) && data.label.length > 0) {
      const primary = data.label[0];
      const additionalCount = data.label.length - 1;

      return additionalCount > 0 ? `${primary} (+${additionalCount})` : primary;
    }

    return null;
  }, [data?.label]);

  return (
    <>
      {/* Delete button - positioned above edge label */}
      {editable && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -100%) translate(${labelX}px, ${labelY - 20}px)`,
              zIndex: showDeleteConfirm ? 9999 : 999,
              pointerEvents: 'all',
            }}
          >
            {!showDeleteConfirm ? (
              <button
                onClick={handleDeleteClick}
                aria-label="Delete relationship"
                data-testid={`delete-edge-btn-${id}`}
                className="flex cursor-pointer items-center justify-center rounded-full bg-red-500 shadow-lg transition-colors hover:bg-red-600"
                style={{ width: '24px', height: '24px' }}
              >
                <TrashIcon className="h-4 w-4 text-white" aria-hidden="true" />
              </button>
            ) : (
              <div
                className="bg-chariot-background-primary border-chariot-stroke flex flex-col gap-2 rounded p-2 shadow-lg"
                style={{ minWidth: '180px' }}
                onClick={e => e.stopPropagation()}
              >
                <span className="text-chariot-text-secondary text-xs">
                  Delete this relationship?
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={handleDeleteConfirm}
                    data-testid={`confirm-delete-edge-${id}`}
                    className="flex-1 rounded bg-red-500 px-2 py-1 text-xs text-white transition-colors hover:bg-red-600"
                  >
                    Yes
                  </button>
                  <button
                    onClick={handleDeleteCancel}
                    data-testid={`cancel-delete-edge-${id}`}
                    className="bg-chariot-background-tertiary text-chariot-text-primary hover:bg-chariot-background-quaternary flex-1 rounded px-2 py-1 text-xs transition-colors"
                  >
                    No
                  </button>
                </div>
              </div>
            )}
          </div>
        </EdgeLabelRenderer>
      )}

      {/* Edge path group */}
      <g data-testid={`flow-edge-${id}`}>
        {/* Wide invisible clickable area (20px) */}
        <path
          d={edgePath}
          strokeWidth={20}
          className="cursor-pointer fill-none stroke-transparent"
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />

        {/* Visible narrow path with hover effect */}
        <path
          id={id}
          d={edgePath}
          strokeWidth={3}
          className="pointer-events-none fill-none transition-colors"
          style={{
            stroke: isHovered
              ? 'rgb(var(--chariot-brand-primary))'
              : 'rgb(var(--chariot-stroke))',
          }}
          {...(direction === 'source'
            ? { markerStart: isHovered ? 'url(#arrow-hover)' : 'url(#arrow)' }
            : { markerEnd: isHovered ? 'url(#arrow-hover)' : 'url(#arrow)' })}
        />
      </g>

      {/* Edge label - always visible when labelText exists */}
      {labelText && (
        <EdgeLabelRenderer>
          <div
            onClick={handleClick}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
            }}
            className="text-chariot-text-primary bg-chariot-background-primary border-chariot-stroke cursor-pointer rounded border px-2 py-1 text-xs font-medium whitespace-nowrap"
          >
            {labelText}
          </div>
        </EdgeLabelRenderer>
      )}

      {/* Edge details modal - rendered below the edge when expanded */}
      {isExpanded && flatEdgeData && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, 0%) translate(${labelX}px, ${labelY + 48}px)`,
              pointerEvents: 'all',
              zIndex: 1000,
            }}
          >
            <EdgeDetailsModal
              key={id}
              edgeId={id}
              relationshipDetails={flatEdgeData}
              isExpanded={true}
              editable={editable && !!context}
              position={{ x: 0, y: 0 }}
              onClose={() => onToggleModal?.()}
            />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
